import { Container } from '@mui/material';
import * as React from 'react';
import EmailIcon from '@mui/icons-material/Email';
import { appetizers, burgers, foodNote } from '../data/food';
import { whiskeyCategories, whiskeys } from '../data/whiskeys';
import { useInView } from '../hooks/useInView';

// Optimized image helper (lazy + async decode)
const barImg = (name, alt, extra = {}) => (
  <img
    src={process.env.PUBLIC_URL + 'images/' + name}
    alt={alt}
    loading="lazy"
    decoding="async"
    {...extra}
  />
);

export const Home = () => {
  // Whiskey Bible explorer state
  const [query, setQuery] = React.useState('');
  const [activeCat, setActiveCat] = React.useState('All');

  const filteredWhiskeys = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return whiskeys.filter((w) => {
      const catMatch = activeCat === 'All' || w.category === activeCat;
      const textMatch = !q || w.name.toLowerCase().includes(q);
      return catMatch && textMatch;
    });
  }, [query, activeCat]);

  // Scroll-triggered flow animations (lightweight, elegant)
  const [foodRef, foodInView] = useInView({ threshold: 0.08 });
  const [whiskeyRef, whiskeyInView] = useInView({ threshold: 0.1 });
  const [galleryRef, galleryInView] = useInView({ threshold: 0.1 });
  const [locationRef, locationInView] = useInView({ threshold: 0.15 });

  return (
    <div style={{ padding: 0, margin: 0, overflow: 'hidden' }}>
      {/* HERO — strong atmospheric opener */}
      <section id="top" className="rn-hero">
        {barImg('front.webp', 'The Rusty Nail interior', { style: { objectPosition: '50% 38%' } })}
        <div className="rn-hero-content hero-entrance">
          <h1 className="hero-title">THE RUSTY NAIL</h1>
          <div className="tag hero-tag">San Antonio, Texas</div>

          <div className="hero-ctas" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="#food" className="linkDestyle" style={{ background: '#c5a26f', color: '#111', padding: '10px 24px', fontSize: 13, letterSpacing: 1.5, fontWeight: 600 }}>
              SEE THE MENU
            </a>
            <a href="#whiskey" className="linkDestyle" style={{ border: '1px solid #c5a26f', color: '#c5a26f', padding: '10px 24px', fontSize: 13, letterSpacing: 1.5 }}>
              WHISKEY BIBLE
            </a>
          </div>
          <div className="scroll-hint">SCROLL TO BEGIN</div>
        </div>
      </section>

      <div className="rn-location-strip">15122 POTRANCO RD • SAN ANTONIO, TX 78245</div>

      {/* FOOD — data driven, clean cards */}
      <section id="food" className="rn-section" ref={foodRef}>
        <Container>
          <div className={`flow-reveal fade-in ${foodInView ? 'is-visible' : ''}`}>
            <div className="rn-section-header">KITCHEN</div>
            <h2 style={{ margin: '6px 0 8px', fontSize: 28 }}>Food</h2>
            <p className="rn-subhead">Texas comfort done right. Smoked brisket, house sauces, and made-to-order classics.</p>
          </div>

          {/* Appetizers */}
          <div style={{ marginTop: 38 }}>
            <div className={`rn-banner flow-reveal fade-in ${foodInView ? 'is-visible' : ''}`}>APPETIZERS</div>
            <div className={`rn-food-grid stagger ${foodInView ? 'is-visible' : ''}`}>
              {appetizers.map((item, i) => (
                <div
                  key={i}
                  className={`rn-menu-card flow-reveal fade-up ${item.img ? ' has-photo' : ''} ${foodInView ? 'is-visible' : ''}`}
                >
                  {item.img && barImg(item.img, item.name, { style: { aspectRatio: '1 / 1' } })}
                  <div>
                    <div className="rn-menu-name">{item.name}</div>
                    <div className="rn-menu-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Burgers */}
          <div style={{ marginTop: 52 }}>
            <div className={`rn-banner flow-reveal fade-in ${foodInView ? 'is-visible' : ''}`}>BURGERS &amp; SANDWICHES</div>
            <div className={`rn-food-grid stagger ${foodInView ? 'is-visible' : ''}`}>
              {burgers.map((item, i) => (
                <div
                  key={i}
                  className={`rn-menu-card flow-reveal fade-up ${item.img ? ' has-photo' : ''} ${foodInView ? 'is-visible' : ''}`}
                >
                  {item.img && barImg(item.img, item.name, { style: { aspectRatio: '1 / 1' } })}
                  <div>
                    <div className="rn-menu-name">{item.name}</div>
                    <div className="rn-menu-desc">{item.desc}</div>
                    {item.note && <div style={{ fontSize: 12, marginTop: 4, color: '#8c6640' }}>{item.note}</div>}
                  </div>
                </div>
              ))}
            </div>
            <div className={`rn-menu-note flow-reveal fade-in ${foodInView ? 'is-visible' : ''}`}>{foodNote}</div>
          </div>
        </Container>
      </section>

      {/* WHISKEY BIBLE — new centerpiece, interactive & optimized */}
      <section id="whiskey" className="rn-section" style={{ paddingTop: 22, paddingBottom: 62 }} ref={whiskeyRef}>
        <Container>
          <div className={`flow-reveal fade-up ${whiskeyInView ? 'is-visible' : ''}`}>
            <div className="rn-section-header">THE COLLECTION</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 28 }}>Whiskey Bible</h2>
              <span style={{ color: '#c5a26f', fontSize: 13 }}>700+ bottles</span>
            </div>
            <p className="rn-subhead" style={{ marginTop: 6 }}>
              One of the largest and most curated whiskey selections in Texas.
            </p>
          </div>

          <div className={`rn-whiskey flow-reveal fade-up ${whiskeyInView ? 'is-visible' : ''}`} style={{ marginTop: 24, padding: 18 }}>
            <div className="rn-whiskey-controls">
              <input
                type="text"
                className="rn-search"
                placeholder="Search by name (Eagle Rare, Balcones, Stagg, etc.)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Filter whiskey list"
              />
              <div className="rn-count">{filteredWhiskeys.length} shown</div>
            </div>

            <div className="rn-filter">
              {whiskeyCategories.map((cat) => (
                <button
                  key={cat}
                  className={'rn-pill' + (activeCat === cat ? ' active' : '')}
                  onClick={() => setActiveCat(cat)}
                  type="button"
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="rn-whiskey-list" role="list">
              {filteredWhiskeys.length ? (
                filteredWhiskeys.slice(0, 240).map((w, idx) => (
                  <div
                    key={idx}
                    className={`rn-whiskey-row flow-reveal fade-in ${whiskeyInView ? 'is-visible' : ''}`}
                    style={whiskeyInView && idx < 14 ? { transitionDelay: `${60 + idx * 18}ms` } : undefined}
                    role="listitem"
                  >
                    <span className="rn-whiskey-name">{w.name}</span>
                    <span className="rn-whiskey-cat">{w.category}</span>
                  </div>
                ))
              ) : (
                <div style={{ padding: 22, color: '#a89f8f' }}>No results. Clear filters or try another term.</div>
              )}
            </div>
            {filteredWhiskeys.length > 240 && (
              <div className="rn-whiskey-note">Showing first 240. Narrow your search or category for more.</div>
            )}
            <div className="rn-whiskey-note">
              The complete list lives at the bar — new barrels and limited releases arrive often.
            </div>
          </div>
        </Container>
      </section>

      {/* ATMOSPHERE GALLERY */}
      <section id="spirits" className="rn-section" style={{ paddingTop: 12 }} ref={galleryRef}>
        <Container>
          <div className={`flow-reveal fade-in ${galleryInView ? 'is-visible' : ''}`}>
            <div className="rn-section-header">THE ROOM</div>
            <h2 style={{ margin: '2px 0 4px' }}>Atmosphere</h2>
            <p className="rn-subhead">Low lights. Good conversation. The kind of place you don’t want to leave.</p>
          </div>
        </Container>

        <div style={{ marginTop: 22 }}>
          <div className={`rn-gallery stagger ${galleryInView ? 'is-visible' : ''}`}>
            <div className={`flow-reveal fade-scale ${galleryInView ? 'is-visible' : ''}`}>{barImg('1225.webp', 'Bar detail')}</div>
            <div className={`flow-reveal fade-scale ${galleryInView ? 'is-visible' : ''}`}>{barImg('1552.webp', 'Seating and atmosphere')}</div>
            <div className={`flow-reveal fade-scale ${galleryInView ? 'is-visible' : ''}`}>{barImg('1051.webp', 'Bottles behind the bar')}</div>
            <div className={`flow-reveal fade-scale ${galleryInView ? 'is-visible' : ''}`}>{barImg('13.webp', 'Wide view of The Rusty Nail')}</div>
            <div className={`flow-reveal fade-scale ${galleryInView ? 'is-visible' : ''}`}>{barImg('0314.webp', 'Rusty Nail exterior')}</div>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section id="location" className="rn-section" style={{ background: '#111', padding: '52px 0 36px' }} ref={locationRef}>
        <Container>
          <div className={`flow-reveal fade-up ${locationInView ? 'is-visible' : ''}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 36, alignItems: 'start' }}>
            <div>
              <div className="rn-section-header" style={{ color: '#c5a26f' }}>FIND US</div>
              <h2 style={{ marginTop: 2 }}>Location</h2>
              <div style={{ margin: '16px 0', lineHeight: 1.65 }}>
                15122 Potranco Rd<br />
                San Antonio, TX 78245
              </div>
              <a href="https://goo.gl/maps/NBEF1seiiqLiz9Pu7" target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                Directions →
              </a>
              <div style={{ marginTop: 22, fontSize: 13, color: '#a89f8f' }}>(210) 254-9201</div>
            </div>

            <iframe
              title="The Rusty Nail on Google Maps"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3475.0729822449894!2d-98.79894238334165!3d29.426663282111292!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x865c414435467dc5%3A0x8f3d7d2901e64802!2sThe%20Rusty%20Nail!5e0!3m2!1sen!2sus!4v1650120798883!5m2!1sen!2sus"
              width="100%"
              height="250"
              style={{ border: 0, background: '#0a0805' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rn-map"
            />
          </div>
        </Container>
      </section>

      {/* CONTACT */}
      <section id="contact" className="rn-section" style={{ padding: '28px 0 60px' }}>
        <Container>
          <div className={`rn-contact flow-reveal fade-up ${locationInView ? 'is-visible' : ''}`}>
            <EmailIcon style={{ fontSize: 40, marginBottom: 10, opacity: 0.8 }} />
            <div>
              Private events or questions?{' '}
              <a href="mailto:contact@therustynailsa.com" className="linkDestyle email">contact@therustynailsa.com</a>
            </div>
          </div>
        </Container>
      </section>

      {/* Closing image */}
      <div style={{ lineHeight: 0 }}>
        {barImg('0314.webp', 'The Rusty Nail', { style: { width: '100%', display: 'block' } })}
      </div>
    </div>
  );
};
